import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NamedEntity } from "../../shared/CaseModel";
import { DialogShell } from "../general/DialogShell";

export interface CopyRequest {
  readonly sourcePartyId: string;
  readonly scaling: number;
}

export interface CostCopyDialogProps {
  target: string;
  sources?: NamedEntity[];
  onClose: (copy?: CopyRequest) => void;
  open: boolean;
}

export const CostCopyDialog = ({
  target,
  sources = [],
  onClose,
  open,
}: CostCopyDialogProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.budget.costCopyDialog",
  });
  const [sourcePartyId, setSourcePartyId] = useState<string>();
  const handleSourceChange = (
    event: SelectChangeEvent<typeof sourcePartyId>,
  ) => {
    const {
      target: { value },
    } = event;
    setSourcePartyId(
      // On autofill we get a stringified value.
      typeof value === "string" ? value : undefined,
    );
  };

  const canSubmit = sourcePartyId !== undefined;

  return (
    <DialogShell
      open={open}
      onClose={() => onClose()}
      title={t("title")}
      description={t("description", { target })}
      actions={
        <>
          <Button onClick={() => onClose()} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            onClick={() => {
              if (sourcePartyId === undefined) {
                onClose();
                return;
              }
              onClose({ sourcePartyId, scaling: 1 });
            }}
            variant="contained"
            disabled={!canSubmit}
          >
            {t("submit")}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {t("source.description")}
      </Typography>
      <FormControl
        key={"source-selection"}
        fullWidth
        size="small"
        error={sourcePartyId === undefined}
      >
        <InputLabel id="source-select-label">{t("source.label")}</InputLabel>
        <Select
          disabled={sources.length === 0}
          label={t("source.label")}
          onChange={handleSourceChange}
          labelId="source-select-label"
          value={sourcePartyId === undefined ? "" : sourcePartyId}
          renderValue={(selected) =>
            sources.find((p) => selected === p.id)?.name
          }
        >
          {sources.map((party) => (
            <MenuItem key={party.id} value={party.id}>
              {party.name}
            </MenuItem>
          ))}
        </Select>
        {sourcePartyId === undefined && (
          <FormHelperText>{t("source.empty")}</FormHelperText>
        )}
      </FormControl>
    </DialogShell>
  );
};
